import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode, type ReactElement } from 'react';

/**
 * Label, hint and error wired to one input through `aria-describedby`.
 *
 * A visually adjacent error message is not an error message for anyone using a
 * screen reader — it is unrelated text somewhere on the page. The wiring is
 * done here, once, so no form has to remember it: `aria-invalid` marks the
 * field, `aria-describedby` points at whichever of hint/error exist, and
 * `role="alert"` makes a new error announce itself.
 */
export interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  children(props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean;
  }): ReactNode;
}

export function Field({ label, hint, error, required = false, children }: FieldProps): ReactElement {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-[var(--space-1)]">
      <label htmlFor={id} className="text-[length:var(--text-sm)] font-medium text-body">
        {label}
        {required ? (
          // `danger-text`, not `danger`: §16.6 wants 4.5:1 at this size and the
          // fill token measures 4.44:1 on the surface behind it.
          <span aria-hidden="true" className="ml-0.5 text-danger-text">
            *
          </span>
        ) : null}
      </label>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required,
      })}

      {hint ? (
        <p id={hintId} className="text-[length:var(--text-xs)] text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        // The glyph is what stops the error from depending on hue alone
        // (WCAG 1.4.1) — the same icon-plus-words pairing the pills use, and
        // the same reason: red-on-pale is not a channel every reader has.
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1 text-[length:var(--text-xs)] text-danger-text"
        >
          <CircleAlert aria-hidden="true" size={12} strokeWidth={2.25} className="mt-[2px] shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
